import React, { useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useVillain } from '../context/VillainContext';

const villainQuotes = [
  "Your villain doesn't need motivation. They just do it.",
  "Your villain already has everything you want.",
  "Your villain is working while you're scrolling.",
  "Your villain doesn't make excuses.",
  "Your villain embraces discomfort as a path to growth.",
  "Your villain is consistent, even when it's inconvenient.",
  "Your villain doesn't wait for perfect conditions.",
  "Your villain turns obstacles into opportunities.",
  "Your villain doesn't seek validation from others.",
  "Your villain is disciplined when no one is watching."
];

const VillainCreator = ({ onClose }) => {
  const { villain, updateAttribute, setVillainName, createVillain } = useVillain();
  const [currentStep, setCurrentStep] = useState(villain.created ? 'complete' : 'name');
  const [attributeCount, setAttributeCount] = useState(villain.attributes.length || 5);

  const handleNameSubmit = (e) => {
    e.preventDefault();
    setCurrentStep('attributes');
  };

  const handleAttributesSubmit = (e) => {
    e.preventDefault();
    createVillain();
    setCurrentStep('complete');
  };

  const addMoreAttributes = () => {
    if (attributeCount < 20) {
      setAttributeCount(prev => prev + 1);
    }
  };

  const getRandomQuote = () => {
    return villainQuotes[Math.floor(Math.random() * villainQuotes.length)];
  };

  return (
    <VillainContainer>
      <VillainHeader>
        <VillainTitle>Describe Your Villain</VillainTitle>
        <CloseButton onClick={onClose}>&times;</CloseButton>
      </VillainHeader>

      <VillainContent>
        {currentStep === 'name' && (
          <NameSection>
            <VillainIntro>
              Create your arch nemesis - the person who pushes you to be better.
              This villain has everything you want and does everything you should be doing.
            </VillainIntro>

            <QuoteBox>
              <Quote>"{getRandomQuote()}"</Quote>
            </QuoteBox>

            <Form onSubmit={handleNameSubmit}>
              <Label>
                Name your villain:
                <Input
                  type="text"
                  value={villain.name}
                  onChange={(e) => setVillainName(e.target.value)}
                  placeholder="e.g., Shadow Self, Future Me, The Competitor"
                  required
                />
              </Label>
              <Button type="submit">Next</Button>
            </Form>
          </NameSection>
        )}

        {currentStep === 'attributes' && (
          <AttributesSection>
            <VillainIntro>
              Define what makes {villain.name} your ultimate competitor.
              List up to 20 attributes, habits, or achievements that push you to be better.
            </VillainIntro>

            <QuoteBox>
              <Quote>"{getRandomQuote()}"</Quote>
            </QuoteBox>

            <Form onSubmit={handleAttributesSubmit}>
              {Array.from({ length: attributeCount }).map((_, index) => (
                <Label key={index}>
                  Attribute {index + 1}:
                  <Input
                    type="text"
                    value={villain.attributes[index] || ''}
                    onChange={(e) => updateAttribute(index, e.target.value)}
                    placeholder={`e.g., Wakes up at 5 AM every day`}
                    required={index < 3} // First 3 attributes required
                  />
                </Label>
              ))}

              {attributeCount < 20 && (
                <AddButton type="button" onClick={addMoreAttributes}>
                  + Add Another Attribute
                </AddButton>
              )}

              <Button type="submit">Create My Villain</Button>
            </Form>
          </AttributesSection>
        )}

        {currentStep === 'complete' && (
          <CompleteSection>
            <VillainIntro>
              Meet your arch nemesis: <VillainName>{villain.name}</VillainName>
            </VillainIntro>

            <AttributesList>
              {villain.attributes.filter(attr => attr).map((attribute, index) => (
                <AttributeItem key={index}>
                  <AttributeNumber>{index + 1}</AttributeNumber>
                  <AttributeText>{attribute}</AttributeText>
                </AttributeItem>
              ))}
            </AttributesList>

            <QuoteBox>
              <Quote>
                "Every time you make an excuse, {villain.name} gains an advantage.
                Every time you take action, you close the gap."
              </Quote>
            </QuoteBox>

            <ButtonGroup>
              <Button onClick={() => setCurrentStep('attributes')}>Edit Villain</Button>
              <Button onClick={onClose}>Return to Quotes</Button>
            </ButtonGroup>
          </CompleteSection>
        )}
      </VillainContent>
    </VillainContainer>
  );
};

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const VillainContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.95);
  z-index: 1000;
  overflow-y: auto;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  animation: ${css`${fadeIn} 0.3s ease-in-out`};
`;

const VillainHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  border-bottom: 1px solid #d4af37;
  padding-bottom: 1rem;
`;

const VillainTitle = styled.h2`
  font-family: var(--font-primary);
  font-size: 2rem;
  color: #d4af37;
  background: linear-gradient(to right, #BF953F, #FCF6BA, #B38728, #FBF5B7, #AA771C);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-fill-color: transparent;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: var(--color-primary);
  font-size: 2.5rem;
  cursor: pointer;
  transition: color 0.3s ease;

  &:hover {
    color: #d4af37;
  }
`;

const VillainContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  width: 100%;
`;

const VillainIntro = styled.p`
  font-family: var(--font-secondary);
  font-size: 1.2rem;
  color: var(--color-text);
  margin-bottom: 2rem;
  line-height: 1.6;
  text-align: center;
`;

const VillainName = styled.span`
  color: #d4af37;
  font-weight: bold;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-top: 2rem;
`;

const Label = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  font-family: var(--font-secondary);
  color: var(--color-text);
`;

const Input = styled.input`
  padding: 0.8rem;
  background: #111;
  border: 1px solid #333;
  border-radius: 4px;
  color: var(--color-text);
  font-family: var(--font-secondary);

  &:focus {
    outline: none;
    border-color: #d4af37;
  }
`;

const Button = styled.button`
  background: linear-gradient(145deg, #1a1a1a, #0a0a0a);
  color: #d4af37;
  border: 1px solid #d4af37;
  border-radius: 4px;
  padding: 0.8rem 1.5rem;
  font-family: var(--font-secondary);
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1rem;

  &:hover {
    background: #d4af37;
    color: #000;
  }
`;

const AddButton = styled.button`
  background: transparent;
  color: #d4af37;
  border: 1px dashed #d4af37;
  border-radius: 4px;
  padding: 0.8rem;
  font-family: var(--font-secondary);
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(212, 175, 55, 0.1);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 2rem;
`;

const NameSection = styled.div`
  animation: ${css`${fadeIn} 0.5s ease-in-out`};
`;

const AttributesSection = styled.div`
  animation: ${css`${fadeIn} 0.5s ease-in-out`};
`;

const CompleteSection = styled.div`
  animation: ${css`${fadeIn} 0.5s ease-in-out`};
`;

const QuoteBox = styled.div`
  background: linear-gradient(145deg, #1a1a1a, #0a0a0a);
  border-left: 3px solid #d4af37;
  padding: 1.5rem;
  margin: 2rem 0;
  border-radius: 0 4px 4px 0;
`;

const Quote = styled.p`
  font-family: var(--font-primary);
  font-style: italic;
  color: var(--color-text);
  font-size: 1.1rem;
  line-height: 1.6;
`;

const AttributesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 2rem 0;
`;

const AttributeItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  background: linear-gradient(145deg, #1a1a1a, #0a0a0a);
  padding: 1rem;
  border-radius: 4px;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateX(5px);
  }
`;

const AttributeNumber = styled.span`
  background: #d4af37;
  color: #000;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin-right: 1rem;
  flex-shrink: 0;
`;

const AttributeText = styled.span`
  color: var(--color-text);
  font-family: var(--font-secondary);
`;

export default VillainCreator;
